import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Mail, Check, X, Loader2, Unlink, RefreshCw, Info } from 'lucide-react';
import { ConfirmationData, ComparisonField } from '../../types/confirmation';
import { buildComparisonFields, getTradeFixingDates } from '../../utils/confirmationComparison';
import { parseAmountInput, formatPriceDisplay } from '../../utils/amountFormatter';
import { clientService } from '../../services/clientService';
import { useAuth } from '../auth/AuthContext';
import ConfirmDialog from '../common/ConfirmDialog';
import InlineMenu from '../common/InlineMenu';
import './ComparisonTable.css';

interface ComparisonTableProps {
  trade: Record<string, unknown> | null;
  confirmationData: ConfirmationData | null;
  loading: boolean;
  onActionError?: (error: string) => void;
  onActionSuccess?: (message: string) => void;
  onGridRefreshNeeded?: () => void; // Callback to refresh the grid after settlement instruction generation
  selectedField?: string | null; // Currently selected field for highlighting
  onFieldSelected?: (fieldName: string | null) => void; // Callback when a field row is clicked
  readOnly?: boolean; // When true, hide action buttons (e.g. PendingModification trades)
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  trade,
  confirmationData,
  loading,
  onActionError,
  onActionSuccess,
  onGridRefreshNeeded,
  selectedField,
  onFieldSelected,
  readOnly,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.substring(0, 2) || 'es';
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;
  // Use the trade's own entity ID for API calls (supports child entities like LDA SpA under Iansa)
  const tradeClientId = (trade?.organizationId as string) || clientId;

  // Action button loading states
  const [generatingSettlement, setGeneratingSettlement] = useState(false);
  const [downloadingSettlement, setDownloadingSettlement] = useState(false);
  const [generatingMailback, setGeneratingMailback] = useState(false);
  const [unmatching, setUnmatching] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [showUnmatchConfirm, setShowUnmatchConfirm] = useState(false);

  // Fixing dates modal state
  const [showFixingDatesModal, setShowFixingDatesModal] = useState(false);

  // Accept counterparty value state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; field: ComparisonField } | null>(null);
  const [acceptField, setAcceptField] = useState<ComparisonField | null>(null);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [accepting, setAccepting] = useState(false);

  // Settlement type cascade modal state (KAN-197 Point 5)
  const [showCascadeModal, setShowCascadeModal] = useState(false);
  const [cascadeFields, setCascadeFields] = useState<Array<{
    fieldName: string; label: string; value: string | null; computed: boolean;
  }>>([]);
  const [cascadePosition, setCascadePosition] = useState<{ x: number; y: number } | null>(null);

  // Price cascade modal state (Section 9 — Finding 4)
  const [showPriceCascadeModal, setShowPriceCascadeModal] = useState(false);
  const [priceCascadeSpotPrice, setPriceCascadeSpotPrice] = useState('');
  const [priceCascadeForwardPoints, setPriceCascadeForwardPoints] = useState('');
  const [priceCascadeAcceptedPrice, setPriceCascadeAcceptedPrice] = useState<number | null>(null);
  const [priceCascadePosition, setPriceCascadePosition] = useState<{ x: number; y: number } | null>(null);

  // Qty2 cascade modal state
  const [showQty2CascadeModal, setShowQty2CascadeModal] = useState(false);
  const [qty2CascadeSpotPrice, setQty2CascadeSpotPrice] = useState('');
  const [qty2CascadeForwardPoints, setQty2CascadeForwardPoints] = useState('');
  const [qty2CascadeTargetQty2, setQty2CascadeTargetQty2] = useState<number | null>(null);
  const [qty2CascadeTradeQty2, setQty2CascadeTradeQty2] = useState<number | null>(null);
  const [qty2CascadeQty1, setQty2CascadeQty1] = useState<number | null>(null);
  const [qty2CascadePosition, setQty2CascadePosition] = useState<{ x: number; y: number } | null>(null);

  // Role check: only certain roles can accept counterparty values
  const canAcceptCounterpartyValue = ['client_admin', 'client_bo_user', 'client_fo_bo_user'].includes(user?.role || '');

  // Track the storage path of a just-generated settlement instruction (for immediate UI update and re-download)
  const [justGeneratedStoragePath, setJustGeneratedStoragePath] = useState<string | null>(null);

  // Reset local settlement state when trade changes
  useEffect(() => {
    setJustGeneratedStoragePath(null);
  }, [trade?.TradeNumber, trade?.tradeNumber]);

  // Build comparison fields first (needed for hasDiscrepancies check)
  const comparisonFields = useMemo(() => {
    if (!trade || !confirmationData) return [];
    return buildComparisonFields(trade, confirmationData, locale);
  }, [trade, confirmationData, locale]);

  // Check if actions should be enabled
  const isTradeMatched = trade?.status === 'matched' || trade?.status === 'Confirmation OK';
  const hasDiscrepancies = comparisonFields.some(f => f.comparisonResult === 'mismatch');

  // Check if settlement instruction already exists (from DB or just generated in this session)
  const hasSettlementInstruction = !!(trade?.settlementInstructionStoragePath) || !!justGeneratedStoragePath || trade?.settlementInstructionStatus === 'generated';

  // Handle settlement instruction - download if exists, generate if not
  const handleSettlementInstruction = async () => {
    if (!tradeClientId || !trade || !confirmationData) return;

    const emailId = confirmationData.emailId;

    // If settlement instruction already exists, just download it
    if (hasSettlementInstruction) {
      setDownloadingSettlement(true);
      try {
        const storagePath = justGeneratedStoragePath || (trade.settlementInstructionStoragePath as string);
        const urlResult = storagePath
          ? await clientService.getSettlementInstructionUrl(tradeClientId, undefined, storagePath)
          : await clientService.getSettlementInstructionUrl(tradeClientId, emailId);
        if (urlResult.success && urlResult.data?.signed_url) {
          window.open(urlResult.data.signed_url, '_blank');
        } else {
          onActionError?.(t('confirmations.settlementInstructionFailed'));
        }
      } catch {
        onActionError?.(t('confirmations.settlementInstructionFailed'));
      } finally {
        setDownloadingSettlement(false);
      }
      return;
    }

    // Generate new settlement instruction
    setGeneratingSettlement(true);
    try {
      const tradeNumber = (trade.TradeNumber || trade.tradeNumber) as string;
      const bankTradeNumber = confirmationData.extractedFields?.BankTradeNumber;

      const result = await clientService.generateSettlementInstruction(
        tradeClientId,
        tradeNumber,
        bankTradeNumber,
        confirmationData.emailId
      );

      if (result.success) {
        // Store the storage path locally so subsequent clicks can download without error
        const storagePath = result.data?.storage_path;
        if (storagePath) {
          setJustGeneratedStoragePath(storagePath);
        }
        // Refresh the grid to show updated settlement instruction status
        onGridRefreshNeeded?.();
        // The generate endpoint already returns a signed URL in document_url
        if (result.data?.document_url) {
          window.open(result.data.document_url, '_blank');
        }
        // Show success notification after all operations complete
        onActionSuccess?.(t('confirmations.settlementInstructionGenerated'));
      } else {
        onActionError?.(result.message || t('confirmations.settlementInstructionFailed'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.settlementInstructionFailed'));
    } finally {
      setGeneratingSettlement(false);
    }
  };

  // Handle mailback email generation
  const handleMailback = async () => {
    if (!trade || !confirmationData) return;

    setGeneratingMailback(true);
    try {
      const counterpartyName = (trade.CounterpartyName || trade.counterpartyName || 'Counterparty') as string;
      const tradeNumber = (trade.TradeNumber || trade.tradeNumber) as string;
      const toEmail = confirmationData.senderEmail || '';
      const organizationName = user?.organization?.name || 'Our Organization';

      let subject = '';
      let body = '';

      if (hasDiscrepancies) {
        // Email with discrepancies
        subject = t('mailback.subjectDiscrepancy', { tradeNumber });
        body = t('mailback.greeting', { counterpartyName }) + '\n\n';
        body += t('mailback.discrepancyIntro', { tradeNumber, organizationName }) + '\n\n';

        // Add each discrepancy
        comparisonFields
          .filter(f => f.comparisonResult === 'mismatch')
          .forEach(field => {
            body += `${t(`grid.columns.${field.displayName}`)}:\n`;
            body += t('mailback.yourValue') + `: ${field.confirmationValue ?? 'N/A'}\n`;
            body += t('mailback.ourValue') + `: ${field.tradeValue ?? 'N/A'}\n\n`;
          });

        body += t('mailback.regards') + ',\n' + organizationName;
      } else {
        // Confirmation OK email
        subject = t('mailback.subject', { tradeNumber });
        body = t('mailback.greeting', { counterpartyName }) + '\n\n';
        body += t('mailback.confirmationMessage', { tradeNumber, organizationName }) + '\n\n';
        body += t('mailback.regards') + ',\n' + organizationName;
      }

      // Open mailto link
      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);
      window.location.href = `mailto:${toEmail}?subject=${encodedSubject}&body=${encodedBody}`;
      // No toast needed - mailto action is self-evident
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.mailbackFailed'));
    } finally {
      setGeneratingMailback(false);
    }
  };

  // Unmatch guard: check if outbound comms or workflow state prevent unmatching
  const workflowState = (trade?.WorkflowState || trade?.workflowState) as string | undefined;
  const advancedWorkflowStates = ['Confirmed', 'ContractSigned', 'Final'];
  const isWorkflowAdvanced = !!workflowState && advancedWorkflowStates.includes(workflowState);
  const isUnmatchBlocked = isWorkflowAdvanced
    || !!trade?.confirmationEmailSentAt
    || !!trade?.disputeEmailSentAt
    || !!trade?.settlementInstructionSentAt
    || !!trade?.settlementInstructionStoragePath;

  const handleUnmatch = async () => {
    if (!tradeClientId || !trade) return;
    const tradeId = (trade.id || trade.Id) as string;
    if (!tradeId) return;

    setUnmatching(true);
    setShowUnmatchConfirm(false);
    try {
      const result = await clientService.unmatchTrade(tradeClientId, tradeId);
      if (result.success) {
        onActionSuccess?.(t('confirmations.unmatchSuccess'));
        onGridRefreshNeeded?.();
      } else {
        onActionError?.(result.message || t('confirmations.unmatchFailed'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.unmatchFailed'));
    } finally {
      setUnmatching(false);
    }
  };

  // Handle reprocess — re-run matching against the existing email using current trade data
  const handleReprocess = async () => {
    if (!tradeClientId || !confirmationData) return;

    setReprocessing(true);
    try {
      const result = await clientService.reprocessEmail(
        tradeClientId,
        confirmationData.emailId,
        confirmationData.emailTradeIndex,
      );
      if (result.success) {
        onActionSuccess?.(t('confirmations.reprocessSuccess', 'Reprocessed successfully'));
        onGridRefreshNeeded?.();
      } else {
        onActionError?.(result.message || t('confirmations.reprocessFailed', 'Reprocess failed'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.reprocessFailed', 'Reprocess failed'));
    } finally {
      setReprocessing(false);
    }
  };

  // Handle right-click on mismatch rows to show "Accept Counterparty Value" context menu
  const handleRowContextMenu = useCallback((e: React.MouseEvent, field: ComparisonField) => {
    if (readOnly) return;
    if (!canAcceptCounterpartyValue) return;
    if (field.comparisonResult !== 'mismatch') return;

    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, field });
  }, [canAcceptCounterpartyValue, readOnly]);

  // Handle accept counterparty value
  const handleAcceptCounterpartyValue = async () => {
    if (!tradeClientId || !trade || !confirmationData || !acceptField) return;

    const tradeId = (trade.id || trade.Id) as string;
    if (!tradeId) return;

    setAccepting(true);
    setShowAcceptConfirm(false);
    try {
      const result = await clientService.acceptCounterpartyValue(
        tradeClientId,
        tradeId,
        acceptField.fieldName,
        confirmationData.matchId,
      );

      if (result.success && result.data) {
        if (result.data.allDiscrepanciesResolved) {
          onActionSuccess?.(t('confirmations.allDiscrepanciesResolved'));
        } else {
          onActionSuccess?.(t('confirmations.acceptSuccess', { field: t(`grid.columns.${acceptField.displayName}`) }));
        }
        onGridRefreshNeeded?.();
      } else {
        onActionError?.(result.message || t('confirmations.acceptError'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.acceptError'));
    } finally {
      setAccepting(false);
      setAcceptField(null);
    }
  };

  // KAN-197 Point 5: Accept SettlementType with cascade fields
  const handleAcceptWithCascade = async () => {
    if (!tradeClientId || !trade || !confirmationData || !acceptField) return;
    const tradeId = (trade.id || trade.Id) as string;
    if (!tradeId) return;

    setAccepting(true);
    setShowCascadeModal(false);
    try {
      const result = await clientService.acceptSettlementTypeCascade(
        tradeClientId,
        tradeId,
        confirmationData.matchId,
        cascadeFields.map(f => ({ fieldName: f.fieldName, value: f.value })),
      );

      if (result.success && result.data) {
        if (result.data.allDiscrepanciesResolved) {
          onActionSuccess?.(t('confirmations.allDiscrepanciesResolved'));
        } else {
          onActionSuccess?.(t('confirmations.acceptSuccess', { field: t('grid.columns.SettlementType') }));
        }
        onGridRefreshNeeded?.();
      } else {
        onActionError?.(result.message || t('confirmations.acceptError'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.acceptError'));
    } finally {
      setAccepting(false);
      setAcceptField(null);
      setCascadeFields([]);
      setCascadePosition(null);
    }
  };

  // Price cascade: accept Price + SpotPrice + ForwardPoints together
  const handleAcceptPriceCascade = async () => {
    if (!tradeClientId || !trade || !confirmationData) return;
    const tradeId = (trade.id || trade.Id) as string;
    if (!tradeId) return;

    const spot = parseAmountInput(priceCascadeSpotPrice);
    const fwd = parseAmountInput(priceCascadeForwardPoints);
    if (spot === null || fwd === null) return;

    setAccepting(true);
    setShowPriceCascadeModal(false);
    try {
      const result = await clientService.acceptPriceCascade(
        tradeClientId,
        tradeId,
        confirmationData.matchId,
        spot,
        fwd,
      );

      if (result.success && result.data) {
        if (result.data.allDiscrepanciesResolved) {
          onActionSuccess?.(t('confirmations.allDiscrepanciesResolved'));
        } else {
          onActionSuccess?.(t('confirmations.acceptSuccess', { field: t('grid.columns.price') }));
        }
        onGridRefreshNeeded?.();
      } else {
        onActionError?.(result.message || t('confirmations.acceptError'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.acceptError'));
    } finally {
      setAccepting(false);
      setPriceCascadeAcceptedPrice(null);
      setPriceCascadeSpotPrice('');
      setPriceCascadeForwardPoints('');
      setPriceCascadePosition(null);
    }
  };

  // Qty2 cascade: accept QuantityCurrency2 by adjusting SpotPrice + ForwardPoints
  const handleAcceptQty2Cascade = async () => {
    if (!tradeClientId || !trade || !confirmationData) return;
    const tradeId = (trade.id || trade.Id) as string;
    if (!tradeId) return;

    const spot = parseAmountInput(qty2CascadeSpotPrice);
    const fwd = parseAmountInput(qty2CascadeForwardPoints);
    if (spot === null || fwd === null) return;

    setAccepting(true);
    setShowQty2CascadeModal(false);
    try {
      const result = await clientService.acceptPriceCascade(
        tradeClientId,
        tradeId,
        confirmationData.matchId,
        spot,
        fwd,
      );

      if (result.success && result.data) {
        if (result.data.allDiscrepanciesResolved) {
          onActionSuccess?.(t('confirmations.allDiscrepanciesResolved'));
        } else {
          onActionSuccess?.(t('confirmations.acceptSuccess', { field: t('grid.columns.contraAmount') }));
        }
        onGridRefreshNeeded?.();
      } else {
        onActionError?.(result.message || t('confirmations.acceptError'));
      }
    } catch (error) {
      onActionError?.(error instanceof Error ? error.message : t('confirmations.acceptError'));
    } finally {
      setAccepting(false);
      setQty2CascadeTargetQty2(null);
      setQty2CascadeTradeQty2(null);
      setQty2CascadeQty1(null);
      setQty2CascadeSpotPrice('');
      setQty2CascadeForwardPoints('');
      setQty2CascadePosition(null);
    }
  };

  const getStatusIcon = (result: ComparisonField['comparisonResult']) => {
    switch (result) {
      case 'match':
        return <span className="status-icon match">✓</span>;
      case 'mismatch':
        return <span className="status-icon mismatch">✗</span>;
      case 'not_provided':
        return <span className="status-icon not-provided">—</span>;
      case 'not_applicable':
      case 'not_compared':
      default:
        // Empty placeholder to maintain consistent row height
        return <span className="status-icon empty">&nbsp;</span>;
    }
  };

  const getRowClassName = (field: ComparisonField): string => {
    const classes: string[] = ['comparison-row'];

    if (!field.isConfiguredForComparison) {
      classes.push('not-compared');
    }
    if (field.comparisonResult === 'not_applicable') {
      classes.push('not-applicable');
    }
    if (field.comparisonResult === 'mismatch') {
      classes.push('mismatch');
    }
    if (field.comparisonResult === 'not_provided') {
      classes.push('not-provided');
    }
    if (selectedField === field.fieldName) {
      classes.push('selected');
    }

    return classes.join(' ');
  };

  // Handle row click - toggle selection or select new field
  const handleRowClick = (fieldName: string) => {
    // Open fixing dates modal instead of selecting the field
    if (fieldName === 'AverageRateFixingDates') {
      setShowFixingDatesModal(true);
      return;
    }
    if (onFieldSelected) {
      // Toggle off if clicking the same field, otherwise select the new one
      onFieldSelected(selectedField === fieldName ? null : fieldName);
    }
  };

  if (loading) {
    return (
      <div className="comparison-table-container">
        <div className="comparison-header">
          <h3>{t('confirmations.tradeVsConfirmation')}</h3>
        </div>
        <div className="comparison-loading">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!trade) {
    return (
      <div className="comparison-table-container">
        <div className="comparison-header">
          <h3>{t('confirmations.tradeVsConfirmation')}</h3>
        </div>
        <div className="comparison-empty">
          <p>{t('confirmations.selectMatchedTrade')}</p>
        </div>
      </div>
    );
  }

  if (!confirmationData) {
    return (
      <div className="comparison-table-container">
        <div className="comparison-header">
          <h3>{t('confirmations.tradeVsConfirmation')}</h3>
        </div>
        <div className="comparison-empty">
          <p>{t('confirmations.noConfirmationForTrade')}</p>
        </div>
      </div>
    );
  }

  // Price cascade sum validation (live) — parseAmountInput handles both . and , as decimal separator
  const priceCascadeSpotNum = parseAmountInput(priceCascadeSpotPrice);
  const priceCascadeFwdNum = parseAmountInput(priceCascadeForwardPoints);
  const priceCascadeSum = priceCascadeSpotNum !== null && priceCascadeFwdNum !== null
    ? priceCascadeSpotNum + priceCascadeFwdNum
    : NaN;
  const isPriceCascadeValid =
    priceCascadeSpotNum !== null &&
    priceCascadeFwdNum !== null &&
    priceCascadeAcceptedPrice !== null &&
    Math.abs(priceCascadeSum - priceCascadeAcceptedPrice) < 0.001;

  // Qty2 cascade live validation: Qty1 × (Spot + Fwd) ≈ targetQty2 (within 1.0)
  const qty2CascadeSpotNum = parseAmountInput(qty2CascadeSpotPrice);
  const qty2CascadeFwdNum = parseAmountInput(qty2CascadeForwardPoints);
  const qty2CascadeComputed = qty2CascadeSpotNum !== null && qty2CascadeFwdNum !== null && qty2CascadeQty1 !== null
    ? Math.round(qty2CascadeQty1 * (qty2CascadeSpotNum + qty2CascadeFwdNum))
    : NaN;
  const isQty2CascadeValid =
    qty2CascadeSpotNum !== null &&
    qty2CascadeFwdNum !== null &&
    qty2CascadeTargetQty2 !== null &&
    !isNaN(qty2CascadeComputed) &&
    Math.abs(qty2CascadeComputed - qty2CascadeTargetQty2) <= 1.0;

  return (
    <div className="comparison-table-container">
      {/* Loading overlay while generating settlement instruction */}
      {generatingSettlement && (
        <div className="analysis-overlay">
          <div className="analysis-spinner">
            <div className="spinner"></div>
            <p>{t('grid.messages.generatingSettlementInstruction', 'Generating settlement instruction...')}</p>
          </div>
        </div>
      )}
      {downloadingSettlement && (
        <div className="analysis-overlay">
          <div className="analysis-spinner">
            <div className="spinner"></div>
            <p>{t('grid.messages.downloadingSettlementInstruction', 'Downloading...')}</p>
          </div>
        </div>
      )}
      <div className="comparison-header">
        <h3>{t('confirmations.tradeVsConfirmation')}</h3>
        {!readOnly && (
          <div className="comparison-actions">
            <button
              className="action-icon-btn"
              onClick={handleSettlementInstruction}
              disabled={!isTradeMatched || hasDiscrepancies || generatingSettlement || downloadingSettlement}
              title={hasDiscrepancies
                ? t('confirmations.cannotGenerateWithDiscrepancies')
                : hasSettlementInstruction
                  ? t('confirmations.downloadSettlement')
                  : t('confirmations.generateSettlement')}
            >
              {(generatingSettlement || downloadingSettlement) ? (
                <Loader2 size={16} className="spinner-icon" />
              ) : (
                <span className="icon-with-badge">
                  <FileText size={16} />
                  {hasDiscrepancies ? (
                    <span className="badge-error">
                      <X size={8} strokeWidth={3} />
                    </span>
                  ) : hasSettlementInstruction && (
                    <span className="badge-checkmark">
                      <Check size={8} strokeWidth={3} />
                    </span>
                  )}
                </span>
              )}
            </button>
            <button
              className="action-icon-btn"
              onClick={handleMailback}
              disabled={!confirmationData || generatingMailback}
              title={t('confirmations.sendMailback')}
            >
              {generatingMailback ? (
                <Loader2 size={16} className="spinner-icon" />
              ) : (
                <Mail size={16} />
              )}
            </button>
            {isTradeMatched && (
              <button
                className="action-icon-btn"
                onClick={handleReprocess}
                disabled={reprocessing}
                title={t('confirmations.reprocess', 'Reprocess email')}
              >
                {reprocessing ? (
                  <Loader2 size={16} className="spinner-icon" />
                ) : (
                  <RefreshCw size={16} />
                )}
              </button>
            )}
            {isTradeMatched && !isUnmatchBlocked && (
              <button
                className="action-icon-btn unmatch-btn"
                onClick={() => setShowUnmatchConfirm(true)}
                disabled={unmatching}
                title={t('confirmations.unmatch')}
              >
                {unmatching ? (
                  <Loader2 size={16} className="spinner-icon" />
                ) : (
                  <Unlink size={16} />
                )}
              </button>
            )}
          </div>
        )}
      </div>
      {confirmationData.matchReasons?.includes('modification_email_match') && (
        <div className="modification-match-note">
          <Info size={14} />
          <span>{t('confirmations.modificationMatchNote')}</span>
        </div>
      )}
      <div className="comparison-table-wrapper">
        <table className="comparison-table">
          <thead>
            <tr>
              <th>{t('confirmations.field')}</th>
              <th>{t('confirmations.trade')}</th>
              <th>{t('confirmations.confirmation')}</th>
              <th className="status-header"></th>
            </tr>
          </thead>
          <tbody>
            {comparisonFields.map((field) => (
              <tr
                key={field.fieldName}
                className={getRowClassName(field)}
                onClick={() => handleRowClick(field.fieldName)}
                onContextMenu={(e) => handleRowContextMenu(e, field)}
              >
                <td className="comparison-field-name">{t(`grid.columns.${field.displayName}`)}</td>
                <td>{field.tradeValue ?? '—'}</td>
                <td>
                  {field.comparisonResult === 'not_provided' ? '—' : (field.confirmationValue ?? '—')}
                </td>
                <td className="status-cell">
                  {getStatusIcon(field.comparisonResult)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={showUnmatchConfirm}
        title={t('confirmations.unmatchTitle')}
        message={t('confirmations.unmatchMessage')}
        confirmLabel={t('confirmations.unmatch')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleUnmatch}
        onCancel={() => setShowUnmatchConfirm(false)}
        type="warning"
      />

      {/* Accept Counterparty Value — context menu */}
      {contextMenu && (
        <InlineMenu
          items={[
            {
              label: t('confirmations.acceptCounterpartyValue'),
              icon: '✓',
              action: () => {
                setAcceptField(contextMenu.field);
                if (contextMenu.field.fieldName === 'SettlementType') {
                  // KAN-197 Point 5: SettlementType change requires cascade modal
                  const newValue = contextMenu.field.confirmationValue as string;
                  const extracted = confirmationData?.extractedFields;
                  const paymentDate = (trade?.PaymentDate ?? trade?.paymentDate) as string | null;
                  const cascade = newValue === 'Entrega Física' ? [
                    { fieldName: 'SettlementCurrency',   label: 'SettlementCurrency',   value: 'N/A',                                        computed: true  },
                    { fieldName: 'FixingReference',      label: 'FixingReference',      value: 'N/A',                                        computed: true  },
                    { fieldName: 'FixingOffset',         label: 'FixingOffset',         value: 'N/A',                                        computed: true  },
                    { fieldName: 'Currency1PaymentDate', label: 'Currency1PaymentDate', value: extracted?.Currency1PaymentDate ?? null,        computed: false },
                    { fieldName: 'Currency2PaymentDate', label: 'Currency2PaymentDate', value: extracted?.Currency2PaymentDate ?? null,        computed: false },
                  ] : [
                    { fieldName: 'SettlementCurrency',   label: 'SettlementCurrency',   value: extracted?.SettlementCurrency ?? null,          computed: false },
                    { fieldName: 'FixingReference',      label: 'FixingReference',      value: extracted?.FixingReference ?? null,              computed: false },
                    { fieldName: 'FixingOffset',         label: 'FixingOffset',         value: extracted?.FixingOffset ?? null,                 computed: false },
                    { fieldName: 'Currency1PaymentDate', label: 'Currency1PaymentDate', value: paymentDate,                                    computed: true  },
                    { fieldName: 'Currency2PaymentDate', label: 'Currency2PaymentDate', value: paymentDate,                                    computed: true  },
                  ];
                  setCascadeFields(cascade);
                  setCascadePosition({ x: Math.min(contextMenu.x, window.innerWidth - 440), y: contextMenu.y });
                  setContextMenu(null);
                  setShowCascadeModal(true);
                } else if (contextMenu.field.fieldName === 'Price' && trade?.SpotPrice != null) {
                  // Forward trade with SpotPrice decomposition — needs price cascade modal
                  const rawPrice = contextMenu.field.confirmationValue;
                  const acceptedPrice = typeof rawPrice === 'number'
                    ? rawPrice
                    : (parseAmountInput(String(rawPrice ?? '')) ?? 0);
                  setPriceCascadeAcceptedPrice(acceptedPrice);
                  setPriceCascadeSpotPrice(String(trade?.SpotPrice ?? ''));
                  setPriceCascadeForwardPoints(String(trade?.ForwardPoints ?? '0'));
                  setPriceCascadePosition({ x: Math.min(contextMenu.x, window.innerWidth - 440), y: contextMenu.y });
                  setContextMenu(null);
                  setShowPriceCascadeModal(true);
                } else if (contextMenu.field.fieldName === 'QuantityCurrency2' && trade?.SpotPrice != null) {
                  // Forward trade: accept counterparty Qty2 via dedicated Qty2 cascade modal
                  const rawQty2 = contextMenu.field.confirmationValue;
                  const qty2Target = typeof rawQty2 === 'number'
                    ? rawQty2
                    : (parseAmountInput(String(rawQty2 ?? '')) ?? 0);
                  const rawQty2Trade = trade?.QuantityCurrency2;
                  const qty2Trade = typeof rawQty2Trade === 'number'
                    ? rawQty2Trade as number
                    : (parseAmountInput(String(rawQty2Trade ?? '')) ?? 0);
                  const rawQty1 = trade?.QuantityCurrency1;
                  const qty1 = typeof rawQty1 === 'number'
                    ? rawQty1 as number
                    : (parseAmountInput(String(rawQty1 ?? '')) ?? 0);
                  setQty2CascadeTargetQty2(qty2Target);
                  setQty2CascadeTradeQty2(qty2Trade);
                  setQty2CascadeQty1(qty1);
                  setQty2CascadeSpotPrice(String(trade?.SpotPrice ?? ''));
                  setQty2CascadeForwardPoints(String(trade?.ForwardPoints ?? '0'));
                  setQty2CascadePosition({ x: Math.min(contextMenu.x, window.innerWidth - 440), y: contextMenu.y });
                  setContextMenu(null);
                  setShowQty2CascadeModal(true);
                } else {
                  setShowAcceptConfirm(true);
                }
              },
            },
          ]}
          position={{ x: contextMenu.x, y: contextMenu.y }}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Accept Counterparty Value — confirm dialog */}
      <ConfirmDialog
        isOpen={showAcceptConfirm}
        title={t('confirmations.acceptCounterpartyValueTitle')}
        message={
          acceptField
            ? t('confirmations.acceptCounterpartyValueMessage', {
                field: t(`grid.columns.${acceptField.displayName}`),
                tradeValue: acceptField.tradeValue ?? '—',
                counterpartyValue: acceptField.confirmationValue ?? '—',
              })
            : ''
        }
        confirmLabel={t('confirmations.accept')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleAcceptCounterpartyValue}
        onCancel={() => {
          setShowAcceptConfirm(false);
          setAcceptField(null);
        }}
        type="warning"
      />

      {/* KAN-197 Point 5: Settlement Type Cascade Modal */}
      {showCascadeModal && cascadePosition && acceptField && (
        <div
          className="cascade-modal"
          style={{ left: cascadePosition.x, top: cascadePosition.y }}
        >
          <div className="cascade-modal-header">
            <strong>{t('confirmations.settlementTypeCascadeTitle')}</strong>
          </div>
          <div className="cascade-modal-primary">
            {t('grid.columns.settlementType')}: {acceptField.tradeValue ?? '—'} → <strong>{acceptField.confirmationValue ?? '—'}</strong>
          </div>
          <div className="cascade-modal-subtitle">
            {t('confirmations.settlementTypeCascadeSubtitle')}
          </div>
          <div className="cascade-modal-fields">
            {cascadeFields.map((cf, idx) => (
              <div key={cf.fieldName} className="cascade-field-row">
                <label>{t(`grid.columns.${cf.fieldName.charAt(0).toLowerCase() + cf.fieldName.slice(1)}`)}</label>
                {cf.computed ? (
                  <span className="cascade-computed-value">
                    {cf.value ?? '—'} <span className="cascade-computed-tag">{t('confirmations.computed')}</span>
                  </span>
                ) : (
                  <input
                    type="text"
                    value={cf.value ?? ''}
                    onChange={(e) => {
                      setCascadeFields(prev => prev.map((f, i) =>
                        i === idx ? { ...f, value: e.target.value || null } : f
                      ));
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="cascade-modal-actions">
            <button
              className="cascade-cancel-btn"
              onClick={() => {
                setShowCascadeModal(false);
                setAcceptField(null);
                setCascadeFields([]);
                setCascadePosition(null);
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              className="cascade-confirm-btn"
              onClick={handleAcceptWithCascade}
              disabled={accepting}
            >
              {accepting ? <Loader2 size={14} className="spin" /> : null}
              {t('confirmations.accept')}
            </button>
          </div>
        </div>
      )}

      {/* Price Cascade Modal (Section 9 — Finding 4) */}
      {showPriceCascadeModal && priceCascadePosition && (
        <div
          className="cascade-modal"
          style={{ left: priceCascadePosition.x, top: priceCascadePosition.y }}
        >
          <div className="cascade-modal-header">
            <strong>{t('confirmations.priceCascadeTitle')}</strong>
          </div>
          <div className="cascade-modal-primary">
            {t('grid.columns.price')}: {(trade?.Price as number | null) != null ? formatPriceDisplay(trade?.Price as number, locale) : '—'} → <strong>{priceCascadeAcceptedPrice != null ? formatPriceDisplay(priceCascadeAcceptedPrice, locale) : '—'}</strong>
          </div>
          <div className="cascade-modal-subtitle">
            {t('confirmations.priceCascadeSubtitle')}
          </div>
          <div className="cascade-modal-fields">
            <div className="cascade-field-row">
              <label>{t('grid.columns.spotPrice')}</label>
              <input
                type="text"
                value={priceCascadeSpotPrice}
                onChange={(e) => setPriceCascadeSpotPrice(e.target.value)}
              />
            </div>
            <div className="cascade-field-row">
              <label>{t('grid.columns.forwardPoints')}</label>
              <input
                type="text"
                value={priceCascadeForwardPoints}
                onChange={(e) => setPriceCascadeForwardPoints(e.target.value)}
              />
            </div>
            <div className="cascade-field-row">
              <label>{t('confirmations.priceCascadeSum')}</label>
              <span
                className="cascade-computed-value"
                style={{ color: isPriceCascadeValid ? 'green' : 'red' }}
              >
                {isNaN(priceCascadeSum) ? '—' : priceCascadeSum.toFixed(4)}
                {!isPriceCascadeValid && priceCascadeAcceptedPrice !== null && (
                  <span className="cascade-computed-tag">
                    {t('confirmations.priceCascadeSumMismatch', { price: priceCascadeAcceptedPrice })}
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="cascade-modal-actions">
            <button
              className="cascade-cancel-btn"
              onClick={() => {
                setShowPriceCascadeModal(false);
                setPriceCascadeAcceptedPrice(null);
                setPriceCascadeSpotPrice('');
                setPriceCascadeForwardPoints('');
                setPriceCascadePosition(null);
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              className="cascade-confirm-btn"
              onClick={handleAcceptPriceCascade}
              disabled={!isPriceCascadeValid || accepting}
            >
              {accepting ? <Loader2 size={14} className="spin" /> : null}
              {t('confirmations.accept')}
            </button>
          </div>
        </div>
      )}

      {/* Qty2 Cascade Modal */}
      {showQty2CascadeModal && qty2CascadePosition && (
        <div
          className="cascade-modal"
          style={{ left: qty2CascadePosition.x, top: qty2CascadePosition.y }}
        >
          <div className="cascade-modal-header">
            <strong>{t('confirmations.qty2CascadeTitle')}</strong>
          </div>
          <div className="cascade-modal-primary">
            {t('grid.columns.contraAmount')}: {qty2CascadeTradeQty2 != null ? qty2CascadeTradeQty2.toLocaleString() : '—'} → <strong>{qty2CascadeTargetQty2 != null ? qty2CascadeTargetQty2.toLocaleString() : '—'}</strong>
          </div>
          <div className="cascade-modal-subtitle">
            {t('confirmations.qty2CascadeSubtitle')}
          </div>
          <div className="cascade-modal-fields">
            <div className="cascade-field-row">
              <label>{t('grid.columns.spotPrice')}</label>
              <input
                type="text"
                value={qty2CascadeSpotPrice}
                onChange={(e) => setQty2CascadeSpotPrice(e.target.value)}
              />
            </div>
            <div className="cascade-field-row">
              <label>{t('grid.columns.forwardPoints')}</label>
              <input
                type="text"
                value={qty2CascadeForwardPoints}
                onChange={(e) => setQty2CascadeForwardPoints(e.target.value)}
              />
            </div>
            <div className="cascade-field-row">
              <label>{t('confirmations.qty2CascadeComputed')}</label>
              <span
                className="cascade-computed-value"
                style={{ color: isQty2CascadeValid ? 'green' : 'red' }}
              >
                {isNaN(qty2CascadeComputed) ? '—' : qty2CascadeComputed.toLocaleString()}
                {!isQty2CascadeValid && qty2CascadeTargetQty2 !== null && (
                  <span className="cascade-computed-tag">
                    {t('confirmations.qty2CascadeMismatch', { qty2: qty2CascadeTargetQty2.toLocaleString() })}
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className="cascade-modal-actions">
            <button
              className="cascade-cancel-btn"
              onClick={() => {
                setShowQty2CascadeModal(false);
                setQty2CascadeTargetQty2(null);
                setQty2CascadeTradeQty2(null);
                setQty2CascadeQty1(null);
                setQty2CascadeSpotPrice('');
                setQty2CascadeForwardPoints('');
                setQty2CascadePosition(null);
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              className="cascade-confirm-btn"
              onClick={handleAcceptQty2Cascade}
              disabled={!isQty2CascadeValid || accepting}
            >
              {accepting ? <Loader2 size={14} className="spin" /> : null}
              {t('confirmations.accept')}
            </button>
          </div>
        </div>
      )}

      {/* Fixing Dates Comparison Modal */}
      {showFixingDatesModal && trade && confirmationData && (() => {
        // Build trade dates (YYYY-MM-DD → DD/MM/YYYY)
        const tradeDates: string[] = getTradeFixingDates(trade).map(d => {
          const parts = String(d).split('-');
          if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
          if (parts.length === 3 && parts[2].length === 4) return `${parts[0]}/${parts[1]}/${parts[2]}`;
          return String(d);
        });

        // Build confirmation dates (dd-mm-yyyy → DD/MM/YYYY)
        const confirmationDates: string[] = (confirmationData.extractedFields.AverageRateFixingDates || []).map(d => {
          const s = String(d);
          if (/^\d{2}-\d{2}-\d{4}$/.test(s)) { const [day, m, y] = s.split('-'); return `${day}/${m}/${y}`; }
          if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, day] = s.split('-'); return `${day}/${m}/${y}`; }
          return s;
        });

        // Normalize to YYYYMMDD for comparison
        const toKey = (d: string) => { const p = d.split('/'); return p.length === 3 ? `${p[2]}${p[1]}${p[0]}` : d; };
        const tradeSet = new Set(tradeDates.map(toKey));
        const confirmationSet = new Set(confirmationDates.map(toKey));

        // Build unified sorted list
        const allKeys = [...new Set([...tradeSet, ...confirmationSet])].sort();
        const keyToDisplay = (k: string) => `${k.slice(6,8)}/${k.slice(4,6)}/${k.slice(0,4)}`;

        const onlyInTrade = allKeys.filter(k => tradeSet.has(k) && !confirmationSet.has(k)).length;
        const onlyInConfirmation = allKeys.filter(k => !tradeSet.has(k) && confirmationSet.has(k)).length;

        return (
          <div className="alert-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowFixingDatesModal(false)}>
            <div className="fixing-dates-modal">
              <div className="alert-modal-header">
                <h3 className="alert-modal-title">{t('confirmations.fixingDatesComparison', 'Comparación de Fechas de Fixing')}</h3>
                <button className="alert-modal-close" onClick={() => setShowFixingDatesModal(false)}>×</button>
              </div>
              <div className="fixing-dates-summary">
                <span>{t('confirmations.trade')}: <strong>{tradeDates.length}</strong> {t('confirmations.fixingDatesCount', 'fechas')}</span>
                <span>{t('confirmations.confirmation')}: <strong>{confirmationDates.length}</strong> {t('confirmations.fixingDatesCount', 'fechas')}</span>
                {onlyInTrade > 0 && <span className="fixing-diff-badge trade-only">+{onlyInTrade} solo en operación</span>}
                {onlyInConfirmation > 0 && <span className="fixing-diff-badge confirmation-only">+{onlyInConfirmation} solo en confirmación</span>}
                {onlyInTrade === 0 && onlyInConfirmation === 0 && tradeDates.length === confirmationDates.length && (
                  <span className="fixing-diff-badge match">{t('confirmations.fixingDatesIdentical', 'Fechas idénticas')}</span>
                )}
              </div>
              <div className="fixing-dates-table-wrapper">
                <table className="fixing-dates-table">
                  <thead>
                    <tr>
                      <th>{t('confirmations.fixingDate', 'Fecha')}</th>
                      <th>{t('confirmations.trade')}</th>
                      <th>{t('confirmations.confirmation')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allKeys.map(key => {
                      const inTrade = tradeSet.has(key);
                      const inConfirmation = confirmationSet.has(key);
                      const rowClass = inTrade && inConfirmation ? '' : 'fixing-date-mismatch';
                      return (
                        <tr key={key} className={rowClass}>
                          <td>{keyToDisplay(key)}</td>
                          <td className={inTrade ? 'fixing-present' : 'fixing-absent'}>{inTrade ? '✓' : '—'}</td>
                          <td className={inConfirmation ? 'fixing-present' : 'fixing-absent'}>{inConfirmation ? '✓' : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="alert-modal-actions">
                <button className="alert-modal-button primary" onClick={() => setShowFixingDatesModal(false)}>{t('common.close', 'Cerrar')}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ComparisonTable;
